import { scaleLinear } from 'd3-scale';
import { t } from 'i18next';
import { dayjs, Dayjs } from 'lib';
import { ConditionDeviation } from 'queries/conditionDeviation/contract';
import { Coordinate, Section } from 'types';

export const getCssVar = (variable: string) => {
  return window.getComputedStyle(document.documentElement).getPropertyValue(variable);
};

export const scaleCalculator = (values: number[], offset = 0, range = [0, 1]) => {
  let min = 0;
  let max = 0.3;
  if (values.length) {
    [min, max] = [Math.floor(Math.min(...values)), Math.ceil(Math.max(...values))];
  }

  const [startValue, endValue] = [
    min === 0 ? min : min - Math.abs((min / 100) * offset),
    max + Math.abs((max / 100) * offset),
  ];

  return {
    getValueScale: scaleLinear().domain([startValue, endValue]).range(range),
    getScaledValue: scaleLinear().domain(range).range([startValue, endValue]),
  };
};

export const uniqueId = () => {
  return `id${Math.random().toString(16).slice(2)}`;
};

export const getDatetimeFormatByTimeDiff = (
  timeDiff: number,
  unit: 'days' | 'hours' | 'minutes' | 'seconds' = 'hours',
) => {
  let diffInMinutes;
  if (unit === 'seconds') {
    diffInMinutes = timeDiff / 60;
  } else if (unit === 'hours') {
    diffInMinutes = timeDiff * 60;
  } else if (unit === 'days') {
    diffInMinutes = timeDiff * 60 * 24;
  } else {
    diffInMinutes = timeDiff;
  }

  switch (true) {
    case diffInMinutes < 10:
      return 'HH:mm:ss';
    case diffInMinutes < 60 * 24 * 2:
      return 'HH:mm';
    default:
      return t('formats:date.lts');
  }
};

export const convertUTCtoLocalTime = (utcTime?: string): Dayjs =>
  utcTime ? dayjs.utc(utcTime).local() : dayjs();

export const filterUniqueItems = (items: string[], selectedOption: string): string[] => {
  const updatedItens = new Set(items);
  if (updatedItens.has(selectedOption)) {
    updatedItens.delete(selectedOption);
  } else {
    updatedItens.add(selectedOption);
  }
  return [...updatedItens];
};

export const sortBy = <T>(data: T[], comparisonProperty: keyof T, ascendingOrder = true) => {
  return data.sort((a, b) => {
    if (a[comparisonProperty] < b[comparisonProperty]) return ascendingOrder ? -1 : 1;
    if (a[comparisonProperty] > b[comparisonProperty]) return ascendingOrder ? 1 : -1;
    return 0;
  });
};

export const convertBlobToBase64 = async (blob: Blob) => {
  const readFileAsync = () =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const result = await readFileAsync();

  return result;
};

export const formatNumbersWithSign = (number: number) => {
  if (number > 0) return `+${number}`;
  return `${number}`;
};

export const getArrayMedianIndex = (array: unknown[]): number => Math.round((array.length - 1) / 2);

export const removeDuplicates = (array: (number | string)[]): (number | string)[] =>
  array.filter((item, index) => array.indexOf(item) === index);

export const findUniqueElements = (removeEvidences: string[], addEvidences: File[]) => {
  return removeEvidences.filter((evidence) => {
    return !addEvidences.some((addEvidence) => addEvidence.name === evidence);
  });
};

export const returnSameNameFile = (newFile: string, oldFiles: string[]) => {
  return oldFiles.some((oldFile) => newFile === oldFile);
};

export const formatNumberWithDecimalPlaces = (
  number: number,
  decimalPlaces = 1,
  ignoreInteger = true,
): string => number.toFixed(Number.isInteger(number) && ignoreInteger ? 0 : decimalPlaces);

export const colorsPalette = [
  '#5E8EED',
  '#099000',
  '#00B21D',
  '#E6CF00',
  '#D400E7',
  '#FF8A00',
  '#00C9E4',
  '#B0CD00',
  '#6774FF',
  '#B10000',
  '#007E14',
  '#807300',
  '#8E009A',
  '#CC6E00',
  '#00889A',
  '#668000',
  '#5961B1',
  '#FF6565',
  '#65CB76',
  '#474e5a',
  '#B4A200',
  '#AA58B1',
  '#FFAD4D',
  '#59A8B2',
  '#ABBE60',
  '#3F4580',
  '#B15959',
  '#4E9A5A',
  '#CBBF51',
  '#C479CB',
  '#CD9452',
  '#000000',
  '#7AC2CC',
  '#8C985B',
  '#000D99',
  '#B65FCF',
  '#660000',
  '#004B0C',
  '#4CA9FF',
  '#FF008A',
  '#9137EC',
  '#B0B0B0',
  '#BE5504',
  '#0A1172',
  '#666666',
  '#0077E5',
  '#B4367A',
  '#7435B2',
  '#FFFF9F',
  '#32612D',
  '#4E4818',
  '#5D0065',
  '#82EFFD',
  '#7E4400',
  '#005B67',
  '#566615',
  '#D21404',
  '#26294B',
  '#99EDC3',
  '#4E2626',
  '#2E4D33',
  '#FDA172',
  '#65613C',
  '#3944BC',
  '#993232',
  '#3CB043',
  '#643E67',
  '#805626',
  '#BE93D4',
  '#496633',
];

export const objectsArrayToCsv = (objArray: object[]) => {
  const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
  const str =
    `${Object.keys(array[0])
      .map((value) => `"${value}"`)
      .join(',')}` + '\r\n';
  const csvContent = array.reduce(
    (str: string, next: { [s: string]: object } | ArrayLike<object>) => {
      str +=
        `${Object.values(next)
          .map((value) => `"${value}"`)
          .join(',')}` + '\r\n';
      return str;
    },
    str,
  );
  return csvContent;
};

export const objectsArrayToTsv = (objArray: object[]) => {
  const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
  const str =
    `${Object.keys(array[0])
      .map((value) => `"${value}"`)
      .join('\t')}` + '\r\n';
  const csvContent = array.reduce(
    (str: string, next: { [s: string]: object } | ArrayLike<object>) => {
      str +=
        `${Object.values(next)
          .map((value) => `"${value}"`)
          .join('\t')}` + '\r\n';
      return str;
    },
    str,
  );
  return csvContent;
};

export const downloadCsv = (csvContent: string, downloadName: string) => {
  const element = document.createElement('a');
  element.href = `data:text/csv;charset=utf-8,${encodeURI(csvContent)}`;
  element.target = '_blank';
  element.download = downloadName;
  element.click();
};

export const copyTsvToClipboard = (tsvContent: string) => {
  const element = document.createElement('textarea');
  element.value = tsvContent;
  document.body.appendChild(element);
  element.select();
  document.execCommand('copy');
  document.body.removeChild(element);
};

export const generateFileDownload = (file: Blob, fileName: string) => {
  const element = document.createElement('a');
  element.href = URL.createObjectURL(file);
  element.setAttribute('download', fileName);
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
};

export const generateFilePreview = (file: Blob) => {
  window.open(window.URL.createObjectURL(file));
};

export const axesIntersection = (
  p1: Coordinate,
  p2: Coordinate,
  p3: Coordinate,
  p4: Coordinate,
) => {
  const c2x = p3.x - p4.x;
  const c3x = p1.x - p2.x;
  const c2y = p3.y - p4.y;
  const c3y = p1.y - p2.y;

  const d = c3x * c2y - c3y * c2x;

  if (d === 0) {
    return undefined;
  }
  const u1 = p1.x * p2.y - p1.y * p2.x;
  const u4 = p3.x * p4.y - p3.y * p4.x;

  const px = (u1 * c2x - c3x * u4) / d;
  const py = (u1 * c2y - c3y * u4) / d;

  if (
    ((p1.x > p2.x && px < p1.x && px > p2.x) ||
      (p1.x < p2.x && px > p1.x && px < p2.x) ||
      (p1.x === p2.x && px === p1.x && px === p2.x)) &&
    ((p3.y > p4.y && py < p3.y && py > p4.y) ||
      (p3.y < p4.y && py > p3.y && py < p4.y) ||
      (p3.y === p4.y && py === p3.y && py === p4.y)) &&
    ((p1.y > p2.y && py < p1.y && py > p2.y) ||
      (p1.y < p2.y && py > p1.y && py < p2.y) ||
      (p1.y === p2.y && py === p1.y && py === p2.y)) &&
    ((p3.y > p4.y && py < p3.y && py > p4.y) ||
      (p3.y < p4.y && py > p3.y && py < p4.y) ||
      (p3.y === p4.y && py === p3.y && py === p4.y))
  ) {
    return { x: px, y: py };
  }

  return undefined;
};

export const createSection = (data: { x: number | null; y: number | null }[]): Section[] => {
  const response: Section[] = [];
  data.forEach((item: { x: number | null; y: number | null }, i: number) => {
    if (data[i + 1]) {
      response.push({
        x1: Number(item.x),
        y1: Number(item.y),
        x2: Number(data[i + 1].x),
        y2: Number(data[i + 1].y),
      });
    }
  });
  return response;
};

export const timeRangeFormat = (timeRange: number) => {
  switch (true) {
    case timeRange < 24:
      return 'HH:mm';
    case timeRange < 8760:
      return 'DD-MMM HH:mm';
    case timeRange >= 8760:
      return 'DD-MMM-YYYY HH:mm';
    default:
      return 'DD-MMM-YYYY HH:mm';
  }
};

export const debounce = <F extends (...args: Parameters<F>) => ReturnType<F>>(
  func: F,
  waitFor: number,
) => {
  let timeout: NodeJS.Timeout;

  const debounced = (...args: Parameters<F>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), waitFor);
  };

  return debounced;
};

export const areEqual = (array1: string[], array2: string[]) => {
  if (array1.length === array2.length) {
    return array1.every((element) => array2.includes(element));
  }

  return false;
};

export enum EDateFormats {
  API_DATE_FORMAT = 'YYYY-MM-DD',
  MMMYY = 'MMM-YY',
  MONTH_YEAR_FORMAT = 'MM-YYYY',
  DDMMM = 'DD-MMM',
  DDMMMYY = 'DD-MMM-YY',
  MMYY = 'MM/YY',
  DDMMYYYY = 'DD/MM/YYYY',
  DDMMYY = 'DD/MM/YY',
  DDMMMYYYY = 'DD-MMM-YYYY',
  YYYY_MM = 'YYYY-MM',
  TITLE_FORMAT = 'MMMM YYYY',
  FULL_TEXT_FORMAT = 'DD MMMM YYYY',
  YYYYMMDD_HHMMSS = 'YYYY-MM-DD HH:mm:ss',
  DDMMYYYY_HHMMSS = 'DD/MM/YYYY HH:mm:ss',
  DDMMYY_HHMM = 'DD/MM/YY HH:mm',
  DDMM_HH = 'DD/MM HH',
  ABBREVIATED_MONTH = 'MMM',
  DATE_SNIPPET = 'dddd, DD MMM YYYY',
  MMMDDYYYY = 'MMM dd, yyyy',
  DEFAULT_DATE_FORMAT = 'dd-MM-yyyy',
}

export function removeUnderline<T extends Record<string, string>>(
  text: string | T[keyof T],
): string {
  return typeof text === 'string' ? text.replace(/_/g, ' ') : text;
}

const unitsMap: { [key: string]: string } = {
  3: 'k',
  6: 'M',
  9: 'G',
  12: 'T',
  15: 'P',
  18: 'E',
};

const exponents = Object.keys(unitsMap).reverse();

export const formatBigNumbers = (value: string | number, decimalPlaces?: number) => {
  const number = Number(value);
  const exponent = exponents.find((exp) => number >= 10 ** Number(exp));

  if (!exponent) {
    return number.toFixed(decimalPlaces ?? 1);
  }
  if (decimalPlaces) {
    return `${(number / 10 ** Number(exponent)).toFixed(decimalPlaces)}${unitsMap[exponent]}`;
  }
  return `${(number / 10 ** Number(exponent)).toFixed(1)}${unitsMap[exponent]}`.replace('.0', '');
};

export const formatGraphicBigNumbers = (value: string | number) => {
  const number = Number(value);
  const exponent = exponents.find((exp) => number >= 10 ** Number(exp));

  if (!exponent) {
    return String(number);
  }

  return `${(number / 10 ** Number(exponent)).toFixed(1)}${unitsMap[exponent]}`.replace('.0', '');
};

export const formatThousands = (value: string) => {
  return value
    .toString()
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

export function countActionStatus(data: ConditionDeviation[] | undefined): Record<string, number> {
  const statusCounts: Record<string, number> = {
    ACTION_ISSUED: 0,
    COMPLETED: 0,
    INVESTIGATING: 0,
  };

  data?.forEach((item) => {
    statusCounts[item.action_status]++;
  });

  delete statusCounts.CANCELED;

  return statusCounts;
}

export const getUploadFileComment = (category: string, username: string, fileName: string) => {
  switch (category) {
    case 'attached_file':
      return `@${username} attached ${fileName}`;
    case 'detached_file':
      return `@${username} detached ${fileName}`;
    default:
      return '';
  }
};

export const formatOptionLabel = (value: string) => {
  if (!value) return '';

  const withoutUnderlines = removeUnderline(value);

  return withoutUnderlines
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};
